import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { CHECKBOX_EVIDENCE_CARD_LAYOUT } from '@framework/form/item/formItemLayouts.ts';
import { WidgetConfigurationCardGroup } from '@framework/ui/components/configuration-card/widget/WidgetConfigurationCardGroup.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { IconEye, IconPencil } from '@tabler/icons-react';
import { BooleanConditionEditorInput } from '../../../../inputs/expressions-editor/conditions/BooleanConditionEditorInput.tsx';
import { useWidgetSupportedDirectives } from '../../../directives/useWidgetSupportedDirectives.ts';
import { WidgetDirectivesInput } from '../../../directives/WidgetDirectivesInput.tsx';
import { useSectionType } from '../../../sections/SectionTypeContext.tsx';
import { type IWidgetType } from '../../IWidgetType.ts';
import { type Widget, WidgetMainType } from '../../Widget.ts';
import { WidgetConfigurationSlotCards } from './config-slots/WidgetConfigurationSlotCards.tsx';
import { WidgetConfigurationSlotProvider } from './config-slots/WidgetConfigurationSlotContext.tsx';
import { WidgetConfigurationSlotRender } from './config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from './config-slots/WidgetConfigurationSlots.tsx';
import { DisplayRuleConfigSubCard } from './DisplayRuleConfigSubCard.tsx';

export interface IWidgetConfigFormProps {
  widgetType: IWidgetType<Widget> | null | undefined;
  widget: Widget | null | undefined;
  isTableColumn?: boolean;
}

export function WidgetConfigForm(props: IWidgetConfigFormProps) {
  const { widgetType, widget, isTableColumn = false } = props;
  const { sectionType } = useSectionType();

  const supportedDirectives = useWidgetSupportedDirectives(widget, widgetType);

  if (!widget || !widgetType) return null;

  // enabled disponibile solo fuori dalle sezioni documento, per widget non
  // grafici e non "sempre abilitati".
  const showEnabled =
    sectionType !== 'DOCUMENT' &&
    widgetType.mainType !== WidgetMainType.Graphic &&
    !widgetType.alwaysEnabled;

  const configComponentRender = widgetType.configComponent ? (
    <widgetType.configComponent
      widget={widget}
      componentLayout={{ isTableColumn }}
      preview
    />
  ) : null;

  return (
    <>
      <FormErrorsAlert />

      <WidgetConfigurationSlotProvider>
        {/* Produttori: dichiarano i fill, output visivo nullo. Il contenuto
            del configComponent è la card "Impostazioni generali"; per le card
            aggiuntive il componente dichiara i propri
            WidgetConfigurationSlotRender (idem le direttive per slot e ordine). */}
        {configComponentRender && (
          <WidgetConfigurationSlotRender
            slot={WidgetConfigurationSlots.General}
          >
            {configComponentRender}
          </WidgetConfigurationSlotRender>
        )}
        <WidgetDirectivesInput
          directives={supportedDirectives}
          widget={widget}
          widgetType={widgetType}
        />
        {widgetType.rules.length > 0 && (
          <WidgetConfigurationSlotRender
            slot={WidgetConfigurationSlots.Validation}
          >
            {widgetType.rules.map((rule, index) => (
              <rule.configComponent key={index} widget={widget} />
            ))}
          </WidgetConfigurationSlotRender>
        )}
        {!widgetType.hideFlags && (
          <WidgetConfigurationSlotRender
            slot={WidgetConfigurationSlots.Display}
          >
            <DisplayRuleConfigSubCard
              name="visible"
              icon={<TbIcon icon={IconEye} />}
              title="Visibilità"
              alwaysTagLabel="Sempre visibile"
              neverTagLabel="Mai visibile"
            >
              <FormItem
                name="visible"
                label="Visibile"
                layout={CHECKBOX_EVIDENCE_CARD_LAYOUT}
                help="Determina se il campo è mostrato a chi compila; con Regole la visibilità dipende dai valori di altri campi."
              >
                <BooleanConditionEditorInput name="visible" label="Visibile" />
              </FormItem>
            </DisplayRuleConfigSubCard>
            {showEnabled && (
              <DisplayRuleConfigSubCard
                name="enabled"
                icon={<TbIcon icon={IconPencil} />}
                title="Abilitazione"
                alwaysTagLabel="Sempre modificabile"
                neverTagLabel="Mai modificabile"
              >
                <FormItem
                  name="enabled"
                  label="Abilitato"
                  layout={CHECKBOX_EVIDENCE_CARD_LAYOUT}
                  help="Determina se il campo è modificabile: se disattivato resta visibile ma non compilabile."
                >
                  <BooleanConditionEditorInput
                    name="enabled"
                    label="Abilitato"
                  />
                </FormItem>
              </DisplayRuleConfigSubCard>
            )}
          </WidgetConfigurationSlotRender>
        )}

        {/* Gruppo di card denso e collassabile. 
            Le card nascono dai fill registrati, una per slot non vuoto:
            Rendering vero e proprio */}
        <WidgetConfigurationCardGroup dense collapsible>
          <WidgetConfigurationSlotCards />
        </WidgetConfigurationCardGroup>
      </WidgetConfigurationSlotProvider>
    </>
  );
}
